import React from 'react';
import { Link } from 'react-router-dom';
import PaintingHeader from './PaintingHeader';
import PaintingFooter from './PaintingFooter';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { 
  Award, 
  Shield, 
  Users, 
  ThumbsUp, 
  CheckCircle,
  Phone,
  Heart,
  Zap,
  TrendingUp
} from 'lucide-react';

const About = () => {
  const values = [
    {
      icon: <Shield className="w-12 h-12" />,
      title: "Integrity",
      description: "Honest pricing, transparent communication, and reliable service every time."
    },
    {
      icon: <Award className="w-12 h-12" />,
      title: "Quality",
      description: "We use only premium materials and proven techniques for lasting results."
    },
    {
      icon: <Heart className="w-12 h-12" />,
      title: "Care",
      description: "We treat every property like it's our own, with meticulous attention to detail."
    },
    {
      icon: <Zap className="w-12 h-12" />,
      title: "Efficiency",
      description: "On-time completion without compromising quality or cutting corners."
    }
  ];

  const credentials = [
    {
      icon: <Shield className="w-8 h-8 text-white" />,
      title: "Licensed & Insured",
      description: "Fully licensed, bonded, and insured for your complete protection"
    },
    {
      icon: <Award className="w-8 h-8 text-white" />,
      title: "20+ Years Experience",
      description: "Two decades of delivering exceptional painting services"
    },
    {
      icon: <Users className="w-8 h-8 text-white" />,
      title: "Trained Professionals",
      description: "Skilled craftsmen committed to excellence"
    },
    {
      icon: <ThumbsUp className="w-8 h-8 text-white" />,
      title: "100% Satisfaction",
      description: "We don't finish until you're completely happy"
    }
  ];

  const whyChooseUs = [
    "Free, detailed estimates with no hidden fees",
    "Premium quality paints and materials",
    "Clean, professional crew",
    "Flexible scheduling to fit your needs",
    "Complete furniture and floor protection",
    "Same-day cleanup after every project",
    "5-year warranty on exterior work",
    "Local family-owned business",
    "Eco-friendly paint options available",
    "Color consultation included",
    "No job too big or small",
    "Competitive pricing without compromising quality"
  ];

  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      <PaintingHeader />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#1E3A8A] to-[#1E40AF] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-block px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-sm font-semibold mb-4">
            🏆 About Us
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-none mb-6">
            Hollywood's Most Trusted
            <span className="block text-[#EA580C] mt-2">Painting Company</span>
          </h1>
          <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
            Family-owned and operated since 2004, serving Hollywood, FL and surrounding areas with professional painting services.
          </p>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block px-4 py-2 bg-[#F1F5F9] rounded-full text-sm font-bold text-[#1E3A8A] uppercase tracking-wider mb-6">
                Our Story
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-[#0F172A] mb-6">
                Painting Excellence for Over 20 Years
              </h2>
              <div className="space-y-4 text-lg text-[#64748B] leading-relaxed">
                <p>
                  D'este Painting and Service was founded in 2004 by Carlos D'este, who started with a simple mission: to provide Hollywood homeowners with honest, high-quality painting services at fair prices.
                </p>
                <p>
                  What began as a one-person operation has grown into a trusted team of professional painters serving residential and commercial clients throughout South Florida. Despite our growth, we've never lost sight of what matters most—treating every client like family and every project like it's our own home.
                </p>
                <p>
                  Today, we're proud to be Hollywood's go-to painting company, with hundreds of satisfied customers and a reputation built on quality work, integrity, and exceptional service.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <img 
                  src="https://images.unsplash.com/photo-1717281234297-3def5ae3eee1?w=400&h=500&fit=crop" 
                  alt="Painter at work"
                  className="rounded-2xl shadow-xl w-full h-64 object-cover"
                />
                <img 
                  src="https://images.unsplash.com/photo-1562259949-e8e7689d7828?w=400&h=300&fit=crop" 
                  alt="Interior painting"
                  className="rounded-2xl shadow-xl w-full h-48 object-cover"
                />
              </div>
              <div className="space-y-4 pt-8">
                <img 
                  src="https://images.unsplash.com/photo-1638945657536-60259aae3c34?w=400&h=300&fit=crop" 
                  alt="Exterior painting"
                  className="rounded-2xl shadow-xl w-full h-48 object-cover"
                />
                <img 
                  src="https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=400&h=500&fit=crop" 
                  alt="Professional team"
                  className="rounded-2xl shadow-xl w-full h-64 object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 bg-[#FAFAF8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-white rounded-full text-sm font-bold text-[#1E3A8A] uppercase tracking-wider mb-4">
              Our Values
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-[#0F172A] mb-4">
              What Drives Us
            </h2>
            <p className="text-lg text-[#64748B] max-w-2xl mx-auto">
              The principles that guide every project we undertake
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value) => (
              <Card key={value.title} className="text-center border-2 hover:border-[#1E3A8A] transition-colors">
                <CardContent className="p-8">
                  <div className="inline-flex items-center justify-center w-20 h-20 bg-[#1E3A8A] text-white rounded-2xl mb-6">
                    {value.icon}
                  </div>
                  <h3 className="text-xl font-bold text-[#0F172A] mb-3">
                    {value.title}
                  </h3>
                  <p className="text-[#64748B] leading-relaxed">
                    {value.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Credentials */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-[#0F172A] mb-4">
              Why Choose D'este Painting?
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {credentials.map((credential) => (
              <div key={credential.title} className="bg-gradient-to-br from-[#1E3A8A] to-[#1E40AF] rounded-2xl p-6 text-white">
                <div className="mb-4">
                  {credential.icon}
                </div>
                <h3 className="text-lg font-bold mb-2">
                  {credential.title}
                </h3>
                <p className="text-white/90 text-sm">
                  {credential.description}
                </p>
              </div>
            ))}
          </div>

          <Card className="border-2">
            <CardContent className="p-8">
              <h3 className="text-2xl font-bold text-[#0F172A] mb-6 text-center">
                The D'este Difference
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {whyChooseUs.map((item) => (
                  <div key={item} className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-[#EA580C] mt-0.5 flex-shrink-0" />
                    <span className="text-[#0F172A]">{item}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#1E3A8A] to-[#1E40AF] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight mb-6">
            Experience the D'este Difference
          </h2>
          <p className="text-xl text-white/90 mb-8 leading-relaxed">
            Join hundreds of satisfied customers. Get your free quote today!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="w-full sm:w-auto bg-[#EA580C] hover:bg-[#C2410C] text-white text-lg px-8 py-6">
                Request Free Quote
              </Button>
            </Link>
            <a href="tel:+19548172046">
              <Button size="lg" variant="outline" className="w-full sm:w-auto bg-white text-[#1E3A8A] hover:bg-white/90 text-lg px-8 py-6">
                <Phone className="w-5 h-5 mr-2" />
                Call (954) 817-2046
              </Button>
            </a>
          </div>
        </div>
      </section>

      <PaintingFooter />
    </div>
  );
};

export default About;